import { createServerFn } from "@tanstack/react-start";

import { cmsRegistry } from "@/content/cmsRegistry";
import { FEATURE_SLUGS } from "@/content/featurePages";
import { contentSource } from "@/content/source";
import type { Feature, FeatureCard } from "./featureTypes";

const resolve = cmsRegistry<Feature>({
  // Through `contentSource()`, whose dynamic import keeps the CMS client out
  // of the client chunk, see `content/landing/source.ts`.
  load: async () => (await contentSource()).loadFeaturePages(),
  keyOf: (page) => page.slug,
});

/** A feature page's content, from the CMS. */
export const featureContent = createServerFn({ method: "GET" })
  .inputValidator((slug: unknown): string => String(slug ?? ""))
  .handler(({ data }): Promise<Feature | undefined> => resolve(data));

/**
 * Every feature page's card, in the order `FEATURE_SLUGS` publishes them.
 *
 * The cards are authored on the pages they describe, so this is a read of the
 * same CMS query the pages come from rather than a second list. The overview
 * needs it because it shows the cards of pages other than itself, and it takes
 * only the card: the CMS order is document order, which is not the published
 * grid's, so the slug list decides the sequence as it always has.
 */
export const featureCards = createServerFn({ method: "GET" }).handler(
  async (): Promise<Array<FeatureCard>> => {
    const pages = await (await contentSource()).loadFeaturePages();
    const byslug = new Map(pages.map((page) => [page.slug, page]));
    return FEATURE_SLUGS.flatMap((slug) => {
      const page = byslug.get(slug);
      return page ? [{ ...page.card, slug }] : [];
    });
  }
);
